import { useState, useCallback } from 'react';
import { message } from 'antd';
import { d214Api, FC170, FC171, ValidationError } from '../../../services/d214Api';

export function useD214Record() {
  const [record, setRecord] = useState<FC170 | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async (scNo: string, itemNo: number): Promise<FC170 | null> => {
    setLoading(true);
    try {
      const result = await d214Api.get(scNo, itemNo);
      setRecord(result);
      return result;
    } catch {
      message.error('讀取記錄失敗');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const save = useCallback(async (
    master: Partial<FC170>,
    details: Partial<FC171>[],
    isNew: boolean
  ): Promise<{ success: boolean; errors?: ValidationError[] }> => {
    setSaving(true);
    try {
      let result: FC170;
      if (isNew) {
        result = await d214Api.create(master, details);
      } else {
        result = await d214Api.update(master.fc170_01!, master.fc170_02!, master, details);
      }
      setRecord(result);
      message.success(isNew ? '新增成功' : '修改成功');
      return { success: true };
    } catch (err: unknown) {
      const axiosErr = err as { response?: { status: number; data: { errors?: ValidationError[]; error?: string } } };
      if (axiosErr?.response?.status === 422) {
        const errors = axiosErr.response.data.errors || [];
        const msgs = errors.map((e: ValidationError) => e.message).join('；');
        message.error('驗證失敗：' + msgs);
        return { success: false, errors };
      }
      message.error('儲存失敗');
      return { success: false };
    } finally {
      setSaving(false);
    }
  }, []);

  const remove = useCallback(async (scNo: string, itemNo: number): Promise<boolean> => {
    try {
      await d214Api.delete(scNo, itemNo);
      setRecord(null);
      message.success('刪除成功');
      return true;
    } catch {
      message.error('刪除失敗');
      return false;
    }
  }, []);

  return { record, loading, saving, load, save, remove, setRecord };
}
